import { NocfoPlan, NocfoPlanSource } from '@constants'
import { IBusiness, IBusinessUsership, isBusinessBillingLocked } from '@query'

const REFERRAL_REDEEM_PLANS = new Set<NocfoPlan>([NocfoPlan.PLUS, NocfoPlan.PRO])

const isAdminOnBusiness = (business: IBusiness, usership: IBusinessUsership): boolean =>
  usership.business_id === business.slug && usership.permissions.admin.value

const isManualReferralRedeemBusiness = (business: IBusiness): boolean =>
  business.subscription_source === NocfoPlanSource.MANUAL ||
  business.billing_lock_reason === 'manual'

const isStripeReferralRedeemBusiness = (business: IBusiness): boolean => {
  if (business.billing_lock_reason === 'stripe_unpaid') {
    return false
  }

  if (isBusinessBillingLocked(business)) {
    return false
  }

  if (business.stripe_plan_status !== 'active') {
    return false
  }

  return Boolean(business.stripe_customer_id && business.stripe_subscription_id)
}

export const isReferralRedeemEligibleBusiness = (
  business: IBusiness,
  usership: IBusinessUsership
): boolean => {
  if (!isAdminOnBusiness(business, usership)) {
    return false
  }

  if (!REFERRAL_REDEEM_PLANS.has(business.subscription_plan)) {
    return false
  }

  if (business.stripe_plan_status === 'trialing') {
    return false
  }

  if (isManualReferralRedeemBusiness(business)) {
    return true
  }

  return isStripeReferralRedeemBusiness(business)
}

export const getReferralRedeemEligibleBusinesses = (
  businesses: IBusiness[],
  userships: IBusinessUsership[]
): IBusiness[] => {
  const adminUsershipsBySlug = new Map(
    userships
      .filter(usership => usership.permissions.admin.value)
      .map(usership => [usership.business_id, usership])
  )

  return businesses
    .filter(business => {
      const usership = adminUsershipsBySlug.get(business.slug)
      return usership != null && isReferralRedeemEligibleBusiness(business, usership)
    })
    .sort((left, right) => left.name.localeCompare(right.name))
}

export const getDefaultReferralRedeemBusinessSlug = (
  eligibleBusinesses: IBusiness[],
  preferredSlug?: string
): string | undefined => {
  if (eligibleBusinesses.length === 0) {
    return undefined
  }

  if (preferredSlug && eligibleBusinesses.some(business => business.slug === preferredSlug)) {
    return preferredSlug
  }

  return eligibleBusinesses[0].slug
}
